import React, { useState, useEffect, useRef } from 'react';
import { toast } from '@ui/toast/toast';
import { message } from '@ui/i18n/message';

import { VisibilityIcon } from '@ui/icons/material/Visibility';
import { useAuth } from '@common/auth/use-auth';
import { useParams, useNavigate } from 'react-router-dom';
import VideoActionsMenu from './VideoActionsMenu';

interface Clip {
    id: number;
    clip_id: string;
    resolution_1280x720: string;
    resolution_854x480: string;
    video: string;
    gif: string;
    title: string;
    description: string;
    username: string;
    avatar: string;
    views: number;
    likes: number;
    comments: number;
    sharing: number;
    size: number;
    user_id: number;
    category: string;
    location: string;
}

interface ClipsResponse {
  status: string;
  clips: Clip[];
  hasMore: boolean;
  totalClips: number;
  currentPage: number;
}

export function ProfileClipsUsersPanel() {
  const { user } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  const fetchClips = async (page: number) => {
    try {
      const response = await fetch(`/profile-user-clips.php?page=${page}&profile_user_id=${userId}`);
      if (!response.ok) throw new Error('Network response was not ok');
  
      const data: ClipsResponse = await response.json();
      if (data.status === 'success') {
        return data;
      }
      throw new Error('Failed to fetch clips');
    } catch (error) {
      console.error('Error:', error);
      toast.danger(message('Error loading videos'));
      return null;
    }
  };

  const handleUpdate = async () => {
    // Fetch fresh data from the first page
    const data = await fetchClips(1);
    if (data) {
      setClips(data.clips);
      setHasMore(data.hasMore);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const loadInitialClips = async () => {
      const data = await fetchClips(1);
      if (data) {
        setClips(data.clips);
        setHasMore(data.hasMore);
        setCurrentPage(1);
      }
      setLoading(false);
    };

    loadInitialClips();
  }, [userId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore) {
          loadMoreClips();
        }
      },
      { threshold: 0.1 }
    );
  
    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }
  
    observerRef.current = observer;
  
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore]);

  const loadMoreClips = async () => {
    const nextPage = currentPage + 1;
    const data = await fetchClips(nextPage);
  
    if (data) {
      setClips((prevClips) => [...prevClips, ...data.clips]);
      setHasMore(data.hasMore);
      setCurrentPage(nextPage);
    }
  };

  const openClipView = (clip: Clip) => {
    navigate(`/zy/${clip.clip_id}`, {
      state: { clip }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
        {clips.map((clip) => (
          <div
            key={clip.id}
            className="relative aspect-[9/16] rounded-lg overflow-hidden group"
          >
            <div 
              className="absolute inset-0 cursor-pointer"
              onClick={() => openClipView(clip)}
            >
              <img
                src={clip.gif}
                alt={clip.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div 
              className="absolute top-2 right-2 z-5"
              onClick={(e) => e.stopPropagation()}
            >
              <VideoActionsMenu 
                clip={clip} 
                onUpdate={handleUpdate}
              />
            </div>

            <div className="absolute bottom-2 left-2 right-2 flex items-center text-white text-sm z-5">
              <div className="flex items-center space-x-2">
                <VisibilityIcon className="w-[20px] h-[20px]" />
                <span>{clip.views}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Loading reference div for infinite scroll */}
      <div ref={loadingRef} />

      {/* Load More Button */}
      {hasMore && (
        <div className="flex justify-center py-4">
          <button
            onClick={loadMoreClips}
            className="bg-blue-500 hover:bg-blue-600 py-2 px-4 rounded"
          >
            Ver Mais
          </button>
        </div>
      )}

      {/* No More Clips Message */}
      {!hasMore && clips.length > 0 && (
        <div className="flex justify-center py-4">
          <p className="text-gray-500">Não há mais vídeos para exibir.</p>
        </div>
      )}

      {/* No Clips Message */}
      {!loading && clips.length === 0 && (
        <div className="flex justify-center py-4">
          <p className="text-gray-500">Nenhum vídeo encontrado.</p>
        </div>
      )}
    </div>
  );
}

export default ProfileClipsUsersPanel;